import "./App.css";
import Navbar from "@funded-here-interface/common/src/components/MobileNavbar/Navbar";
import { setInvestorToken } from "@funded-here-interface/common/src/features/auth/authSlice_Investor";
import { useVersionCheck } from "@funded-here-interface/common/src/hooks/useVersionCheck";
import { JwtProvider } from "@funded-here-interface/common/src/providers/JwtProvider";
import { SessionInterceptorProvider } from "@funded-here-interface/common/src/providers/SessionInterceptorProvider";
import { Center, Loader } from "@mantine/core";
import { lazy, Suspense } from "react";
import { QueryClientProvider } from "react-query";
import { Provider, useSelector } from "react-redux";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { PersistGate } from "redux-persist/integration/react";

import BorrowerLayout from "./layouts/BorrowerLayout/BorrowerLayout";
import DistributorLayout from "./layouts/DistributorLayout/DistributorLayout";
import IndividualInvestor from "./layouts/IndividualInvestor/IndividualInvestor";
import InvestorLayout from "./layouts/InvestorLayout/InvestorLayout";
import NewOnboardingLayout from "./layouts/OnboardingLayout/NewOnboardingLayout";
import OnboardingLayout from "./layouts/OnboardingLayout/OnboardingLayout";
import { client } from "./reactQuery";
import { store, persistor, RootState } from "./store";
import { investorResetActions } from "./Utils/investorResetActions";

import "react-toastify/dist/ReactToastify.css";

const AcknowledgementLayout = lazy(
  () =>
    import("@funded-here-interface/common/src/Layouts/AcknowledgementLayout/AcknowledgementLayout"),
);
const PasswordReset = lazy(
  () => import("@funded-here-interface/common/src/pages/ForgetPassword/PasswordReset"),
);
const PasswordResetMsg = lazy(
  () => import("@funded-here-interface/common/src/pages/ForgetPassword/PasswordResetMsg"),
);
const TwoFAQRPageCommon = lazy(
  () => import("@funded-here-interface/common/src/pages/TwoFA/TwoFAQRCode"),
);
const MerchantLayout = lazy(() => import("./layouts/OnboardingLayout/MerchantOnboardingLayout"));

const Login = lazy(() => import("./Screens/Login/Login"));
const NewPassword = lazy(() => import("./Screens/NewPassword/NewPassword"));
const PasswordChangedMessage = lazy(() => import("./Screens/NewPassword/PasswordChangedMessage"));
const Registration = lazy(() => import("./Screens/Registration/Registration"));
const WelcomePage = lazy(() => import("./Screens/WelcomePage/WelcomePage"));
const OtpVerification = lazy(() => import("./Screens/OTPverification/OtpVerification"));
const BizProfile = lazy(() => import("./Screens/BizProfile/BizProfile"));
const BankDetails = lazy(() => import("./Screens/BankDetails/BankDetails"));
const InvestorDocsUpload = lazy(() => import("./Screens/DocUpload/InvestorDocsUpload"));
const RepresentativeDetails = lazy(
  () => import("./Screens/RepresentativeDetails/RepresentativeDetails"),
);
const IdentifyMe = lazy(() => import("./Screens/IdentifyMe/IdentifyMe"));
const WelcomeBorrowerPage = lazy(() => import("./Screens/WelcomeBorrower/WelcomeBorrower"));
const BorrowerIdentifyMe = lazy(() => import("./Screens/BorrowerIdentify/BorrowerIdentify"));
const SetupSpvPage = lazy(() => import("./Screens/SettingSPV/SetupSpv"));
const LoanAgreement = lazy(() => import("./Screens/LoanAggrement/LoanAgreement"));
const Deposit = lazy(() => import("./Screens/Deposit/Deposit"));
const BorrowerBizProfile = lazy(() => import("./Screens/BizProfile/BorrowerBizProfile"));
const BorrowerRep = lazy(() => import("./Screens/BorrowerRep/BorrowerRep"));
const BorrowerBank = lazy(() => import("./Screens/BorrowerBank/BorrowerBank"));
const BorrowerDocsUpload = lazy(() => import("./Screens/DocUpload/BorrowerDoc"));
const LoanApplication = lazy(() => import("./Screens/LoanApplication/LoanApplication"));
const WelcomeIssuingSPV = lazy(() => import("./Screens/WelcomeIssuingSPV/WelcomeIssuingSPV"));
const IssuingSPVBank = lazy(() => import("./Screens/IssuingSPVBank/IssuingSPVBank"));
const IssuingSPVBizProfile = lazy(() => import("./Screens/BizProfile/IssuingSPVBizProfile"));
const LTVLoanForm = lazy(() => import("./Screens/LoanForm/LTVLoanForm"));
const LoanAppSku = lazy(() => import("./Screens/LoanApplicationSKU/LoanAppSku"));
const LoanForm = lazy(() => import("./Screens/LoanForm/LoanForm"));
const UploadInvoice = lazy(() => import("./Screens/UploadInvoice/UploadInvoice"));
const StructureNote = lazy(() => import("./Screens/StructuredNote/StructureNote"));
const WelcomeIndividual = lazy(() => import("./Screens/WelcomePage/WelcomeIndividual"));
const InvestorDashboardScreen = lazy(
  () => import("./Screens/Dashboard/InvestorDashboard/InvestorDashboardScreen"),
);
const BizProfileInd = lazy(() => import("./Screens/BizProfile/BizProfileIndividual"));
const BankDetailsInd = lazy(() => import("./Screens/BankDetails/BankDetailsInd"));
const IndInvestorDoc = lazy(() => import("./Screens/DocUpload/IndInvestorDoc"));
const SkuLoanApplication = lazy(() => import("./Screens/SkuLoanApplication/SkuLoanApplication"));
const BMCASigning = lazy(() => import("./Screens/MerchantSigning/BMCASigning"));
const PendingSPV = lazy(() => import("./Screens/PendingSpv/PendingSPV"));
const ConfirmNote = lazy(() => import("./Screens/StructuredNote/ConfirmNote"));
const ShareholderDIDSetup = lazy(() => import("./Screens/ShareholderDIDSetup/ShareholderDIDSetup"));
const D2Zguide = lazy(() => import("./Screens/D2Zguide/Onboardingguide"));
const RegistrationCompleted = lazy(
  () => import("./Screens/RegistrationCompleted/RegistrationCompleted"),
);
const ShareholderSPVBoardResolutionPending = lazy(
  () =>
    import("./Screens/ShareholderSPVBoardResolutionPending/ShareholderSPVBoardResolutionPending"),
);
const PendingShareholderOnboard = lazy(
  () => import("./Screens/PendingShareholderOnboard/PendingShareholderOnboard"),
);
const MerchantCompleteOnboarding = lazy(
  () => import("./Screens/MerchantCompleteOnboarding/MerchantCompleteOnboarding"),
);
const AuthRepDIDSetup = lazy(() => import("./Screens/AuthRepDIDSetup/AuthRepDIDSetup"));
const D2ZguideMerchantPage1 = lazy(() => import("./Screens/D2Zguide/D2ZguideMerchantPage1"));
const D2ZguideMerchantPage2 = lazy(() => import("./Screens/D2Zguide/D2ZguideMerchantPage2"));
const DirectorDIDSetup = lazy(() => import("./Screens/DirectorDIDSetup/DirectorDIDSetup"));
const DirectorSPVBoardResolution = lazy(
  () => import("./Screens/ShareholderSPVBoardResolution/ShareholderSPVBoardResolution"),
);
const DirectorBoardResolution = lazy(
  () => import("./Screens/ShareholderBoardResolution/ShareholderBoardResolution"),
);
const IssuerBoardResolution = lazy(
  () => import("./Screens/IssuerBoardResolution/IssuerBoardResolution"),
);
const FundingIssuingSpvCompleteOnboard = lazy(
  () => import("./Screens/FundingIssuingSpvCompleteOnboard/FundingIssuingSpvCompleteOnboard"),
);
const UnqualifiedInvestor = lazy(() => import("./Screens/UnqualifiedInvestor/UnqualifiedInvestor"));
const AuthRepLoanAgreement = lazy(
  () => import("./Screens/AuthRepLoanAgreement/AuthRepLoanAgreement"),
);
const SignedDocuments = lazy(() => import("./Screens/Merchant/SignedDocuments/SignedDocuments"));
const DeclarationAccreditedInvestor = lazy(
  () => import("./Screens/DeclarationAccreditedInvestor/DeclarationAccreditedInvestor"),
);
const InvestorSignedDocuments = lazy(
  () => import("./Screens/Investor/SignedDocuments/SignedDocuments"),
);
const InstitutionalRep = lazy(() => import("./Screens/InstitutionalRep/InstitutionalRep"));
const IssuerAgreement = lazy(() => import("./Screens/IssuerAgreement/IssuerAgreement"));
const SpvKycPending = lazy(() => import("./Screens/SpvKycPending/SpvKycPending"));
const Withdraw = lazy(() => import("./Screens/Withdraw/Withdraw"));
const RegtankResponsePage = lazy(() => import("./Screens/RegtankResponse/RegtankResponsePage"));
const ThanksPageWrapper = lazy(() => import("./Screens/ThanksPage/ThanksPageWrapper"));
const SupplierDocsUpload = lazy(() => import("./Screens/DocUpload/SupplierDocsUpload"));
const MembershipAgreementPage = lazy(
  () => import("./Screens/MembershipAgreement/MembershipAgreementPage"),
);

type LayoutType =
  | "onboarding"
  | "newOnboarding"
  | "individualInvestor"
  | "borrower"
  | "investor"
  | "distributor";

interface StepperProps {
  step: number;
  showSteps?: boolean;
  totalSteps?: number;
  stepContainerTop?: string;
  stepContainerLeft?: number;
}

interface PrivateRouteConfig {
  path: string;
  component: React.LazyExoticComponent<React.ComponentType>;
  layout?: LayoutType;
  layoutProps?: StepperProps;
}

function renderWithLayout(
  Screen: React.ComponentType,
  layout?: LayoutType,
  layoutProps?: StepperProps,
): React.ReactNode {
  const screenEl = <Screen />;
  if (!layout) return screenEl;

  switch (layout) {
    case "borrower":
      return <BorrowerLayout content={screenEl} />;
    case "investor":
      return <InvestorLayout content={screenEl} />;
    case "onboarding":
      return (
        <OnboardingLayout
          step={layoutProps!.step}
          showSteps={layoutProps!.showSteps}
          totalSteps={layoutProps?.totalSteps}
          stepContainerTop={layoutProps?.stepContainerTop}
          stepContainerLeft={layoutProps?.stepContainerLeft}
          content={screenEl}
        />
      );
    case "newOnboarding":
      return (
        <NewOnboardingLayout
          step={layoutProps!.step}
          showSteps={layoutProps!.showSteps}
          totalSteps={layoutProps?.totalSteps}
          content={screenEl}
        />
      );
    case "individualInvestor":
      return (
        <IndividualInvestor
          step={layoutProps!.step}
          showSteps={layoutProps!.showSteps}
          content={screenEl}
        />
      );
    case "distributor":
      return (
        <DistributorLayout
          step={layoutProps!.step}
          showSteps={layoutProps!.showSteps}
          content={screenEl}
        />
      );
  }
}

const privateRoutes: PrivateRouteConfig[] = [
  // No layout
  { path: "/welcome", component: WelcomePage },
  { path: "/welcome-individual-investor", component: WelcomeIndividual },
  { path: "/welcome-borrower", component: WelcomeBorrowerPage },
  { path: "/biz-profile", component: BizProfile },
  { path: "/representative-details", component: RepresentativeDetails },
  { path: "/bank-details", component: BankDetails },
  { path: "/doc-upload", component: InvestorDocsUpload },
  { path: "/shareholder-did-setup", component: ShareholderDIDSetup },
  { path: "/director-did-setup", component: DirectorDIDSetup },
  { path: "/auth-rep-did-setup", component: AuthRepDIDSetup },
  { path: "/identify-me", component: IdentifyMe },
  { path: "/borrower-identify-me", component: BorrowerIdentifyMe },
  { path: "/setup-spv", component: SetupSpvPage },
  { path: "/loanAgreement", component: LoanAgreement },
  { path: "/deposit", component: Deposit },
  { path: "/loanform", component: LoanForm },
  { path: "/welcome-trustee", component: WelcomeIssuingSPV },
  { path: "/ltvsku", component: LTVLoanForm },
  { path: "/loansku", component: LoanAppSku },
  { path: "/upload-invoice", component: UploadInvoice },
  { path: "/regtank-response", component: RegtankResponsePage },

  // BorrowerLayout (content only)
  { path: "/signed-documents", component: SignedDocuments, layout: "borrower" },
  {
    path: "/new-merchant-onboard",
    component: MerchantLayout,
    layout: "borrower",
  },
  {
    path: "/merchant/onboarding/membership-agreement",
    component: MembershipAgreementPage,
    layout: "newOnboarding",
    layoutProps: { step: 0, showSteps: false },
  },
  { path: "/loanApplication", component: LoanApplication, layout: "borrower" },
  {
    path: "/collaboration-proposal/agreement",
    component: AuthRepLoanAgreement,
    layout: "borrower",
  },
  {
    path: "/collaboration-proposal/:tabValue",
    component: SkuLoanApplication,
    layout: "borrower",
  },

  // InvestorLayout (content only)
  {
    path: "/investor-dashboard/:subpath",
    component: InvestorDashboardScreen,
    layout: "investor",
  },
  {
    path: "/investor-signed-documents",
    component: InvestorSignedDocuments,
    layout: "investor",
  },
  { path: "/note-offer-room", component: StructureNote, layout: "investor" },
  {
    path: "/subscribed-invested-notes",
    component: ConfirmNote,
    layout: "investor",
  },
  { path: "/withdrawal", component: Withdraw, layout: "investor" },

  // NewOnboardingLayout (step=0, showSteps=false)
  {
    path: "/onboarding-guide-investor/:type",
    component: D2Zguide,
    layout: "newOnboarding",
    layoutProps: { step: 0, showSteps: false },
  },
  {
    path: "/onboarding-guide-merchant-loan",
    component: D2ZguideMerchantPage1,
    layout: "newOnboarding",
    layoutProps: { step: 0, showSteps: false },
  },
  {
    path: "/onboarding-guide-merchant-repayment",
    component: D2ZguideMerchantPage2,
    layout: "newOnboarding",
    layoutProps: { step: 0, showSteps: false },
  },
  {
    path: "/director-board-resolution-spv",
    component: DirectorSPVBoardResolution,
    layout: "newOnboarding",
    layoutProps: { step: 0, showSteps: false },
  },
  {
    path: "/director-board-resolution",
    component: DirectorBoardResolution,
    layout: "newOnboarding",
    layoutProps: { step: 0, showSteps: false },
  },
  {
    path: "/issuer-board-resolution",
    component: IssuerBoardResolution,
    layout: "newOnboarding",
    layoutProps: { step: 0, showSteps: false },
  },

  // OnboardingLayout (step=0, showSteps=false)
  {
    path: "/bmca-form",
    component: BMCASigning,
    layout: "onboarding",
    layoutProps: { step: 0, showSteps: false },
  },
  {
    path: "/thanks-page",
    component: ThanksPageWrapper,
    layout: "onboarding",
    layoutProps: { step: 0, showSteps: false, stepContainerTop: "0" },
  },
  {
    path: "/thanks",
    component: ThanksPageWrapper,
    layout: "onboarding",
    layoutProps: { step: 0, showSteps: false, stepContainerTop: "0" },
  },
  {
    path: "/spv-kyc-pending",
    component: SpvKycPending,
    layout: "onboarding",
    layoutProps: { step: 0, showSteps: false, stepContainerTop: "0" },
  },
  {
    path: "/merchant-complete-onboarding",
    component: MerchantCompleteOnboarding,
    layout: "onboarding",
    layoutProps: { step: 0, showSteps: false, stepContainerTop: "0" },
  },
  {
    path: "/registration-completed",
    component: RegistrationCompleted,
    layout: "onboarding",
    layoutProps: { step: 0, showSteps: false, stepContainerTop: "0" },
  },
  {
    path: "/funding-issuing-spv-onboard-completed",
    component: FundingIssuingSpvCompleteOnboard,
    layout: "onboarding",
    layoutProps: { step: 0, showSteps: false, stepContainerTop: "0" },
  },
  {
    path: "/pending-spv-board-resolution",
    component: ShareholderSPVBoardResolutionPending,
    layout: "onboarding",
    layoutProps: { step: 0, showSteps: false, stepContainerTop: "0" },
  },
  {
    path: "/pending-shareholder-onboard",
    component: PendingShareholderOnboard,
    layout: "onboarding",
    layoutProps: { step: 0, showSteps: false, stepContainerTop: "0" },
  },
  {
    path: "/pending-spv",
    component: PendingSPV,
    layout: "onboarding",
    layoutProps: { step: 0, showSteps: false, stepContainerTop: "0" },
  },
  {
    path: "/unqualified-investor",
    component: UnqualifiedInvestor,
    layout: "onboarding",
    layoutProps: { step: 0, showSteps: false, stepContainerTop: "0" },
  },

  // OnboardingLayout (borrower stepper with totalSteps=4)
  {
    path: "/bor-biz-profile",
    component: BorrowerBizProfile,
    layout: "onboarding",
    layoutProps: { step: 1, totalSteps: 4, showSteps: true },
  },
  {
    path: "/bor-rep",
    component: BorrowerRep,
    layout: "onboarding",
    layoutProps: { step: 2, totalSteps: 4, showSteps: true },
  },
  {
    path: "/bor-bank",
    component: BorrowerBank,
    layout: "onboarding",
    layoutProps: { step: 3, totalSteps: 4, showSteps: true },
  },
  {
    path: "/bor-doc",
    component: BorrowerDocsUpload,
    layout: "onboarding",
    layoutProps: { step: 4, totalSteps: 4, showSteps: true },
  },

  // OnboardingLayout (institutional rep)
  {
    path: "/institutional-rep",
    component: InstitutionalRep,
    layout: "onboarding",
    layoutProps: { step: 2, showSteps: true },
  },

  // IndividualInvestor layout
  {
    path: "/ind-biz-profile",
    component: BizProfileInd,
    layout: "individualInvestor",
    layoutProps: { step: 1, showSteps: true },
  },
  {
    path: "/bank-details-ind",
    component: BankDetailsInd,
    layout: "individualInvestor",
    layoutProps: { step: 2, showSteps: true },
  },
  {
    path: "/doc-upload-ind",
    component: IndInvestorDoc,
    layout: "individualInvestor",
    layoutProps: { step: 3, showSteps: true },
  },
  {
    path: "/declaration-ai",
    component: DeclarationAccreditedInvestor,
    layout: "individualInvestor",
    layoutProps: { step: 4, showSteps: false },
  },
  {
    path: "/issuer-agreement",
    component: IssuerAgreement,
    layout: "individualInvestor",
    layoutProps: { step: 4, showSteps: false },
  },

  // DistributorLayout
  {
    path: "/supplier-biz-profile",
    component: IssuingSPVBizProfile,
    layout: "distributor",
    layoutProps: { step: 1, showSteps: true },
  },
  {
    path: "/supplier-bank-details",
    component: IssuingSPVBank,
    layout: "distributor",
    layoutProps: { step: 2, showSteps: true },
  },
  {
    path: "/supplier-doc-upload",
    component: SupplierDocsUpload,
    layout: "distributor",
    layoutProps: { step: 3, showSteps: true },
  },
];

const SuspenseFallback = (
  <Center style={{ height: "100vh" }}>
    <Loader size="lg" />
  </Center>
);

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const token = useSelector((state: RootState) => state.auth.token);

  return !token || token === "none" ? <Navigate to="/login" replace /> : <>{children}</>;
};

function App() {
  useVersionCheck();

  return (
    <QueryClientProvider client={client}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ToastContainer />
          <BrowserRouter>
            <SessionInterceptorProvider
              persistor={persistor}
              resetActions={investorResetActions}
              tokenSelector={(state: RootState) => state.auth}
            >
              <JwtProvider
                tokenSelector={(state: RootState) => state.auth}
                setTokenAction={setInvestorToken}
                resetActions={investorResetActions}
              >
                <Suspense fallback={SuspenseFallback}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/registration" element={<Navbar content={<Registration />} />} />
                    <Route
                      path="/two-auth-QR-code"
                      element={<Navbar content={<TwoFAQRPageCommon />} />}
                    />
                    <Route path="/login/:redirectPath?" element={<Navbar content={<Login />} />} />
                    <Route
                      path="/password-reset"
                      element={<AcknowledgementLayout content={<PasswordReset />} />}
                    />
                    <Route
                      path="/reset-message"
                      element={<AcknowledgementLayout content={<PasswordResetMsg />} />}
                    />
                    <Route
                      path="/reset-password"
                      element={
                        <AcknowledgementLayout
                          content={<NewPassword title="Reset Your Password" />}
                        />
                      }
                    />
                    <Route
                      path="/new-password"
                      element={
                        <AcknowledgementLayout
                          content={<NewPassword title="Password Creation" />}
                        />
                      }
                    />
                    <Route
                      path="/password-changed"
                      element={<AcknowledgementLayout content={<PasswordChangedMessage />} />}
                    />
                    <Route path="/otp-verification" element={<OtpVerification />} />

                    {/* Private routes */}
                    {privateRoutes.map(({ path, component: Screen, layout, layoutProps }) => (
                      <Route
                        key={path}
                        path={path}
                        element={
                          <PrivateRoute>
                            {renderWithLayout(Screen, layout, layoutProps)}
                          </PrivateRoute>
                        }
                      />
                    ))}

                    {/* Catch-all redirect */}
                    <Route path="*" element={<Navigate to="/login" replace />} />
                  </Routes>
                </Suspense>
              </JwtProvider>
            </SessionInterceptorProvider>
          </BrowserRouter>
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  );
}

export default App;
